import React, { ChangeEventHandler, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { noop } from "lodash";
import { Button, Chip, MultiselectOption } from "@shared/components";
import InvestorRequestEntitiesDropdowns from "@investorRequest/components/InvestorRequestEntitiesDropdowns";
import InvestorRequestTextarea from "@investorRequest/components/InvestorRequestTextarea";
import InvestorRequestDatepicker from "@investorRequest/components/InvestorRequestDatepicker";
import SubmitAnotherFormDialog from "@investorRequest/components/SubmitAnotherFormDialog";
import FieldTooltip from "@investorRequest/components/FieldTooltip";
import { selectInternalExternalPresence } from "@store/Entities/ducks";
import { entitledFundsSelector, entitledAccountsSelector } from "@store/Entities/selectors";
import { fetchEntitledAccountsByEntity } from "@store/Entities/thunks";
import { investorRequestSelector } from "@investorRequest/store/selectors";
import {
  updateCommonRequestData,
  updateCustomRequestData,
  clearSubmitSuccess,
  closeInvestorRequestDialog,
} from "@investorRequest/store/ducks";
import { toEffectiveDateString } from "@investorRequest/utils/effectiveDate";
import { useAppDispatch, useAppSelector, useMobileLayout } from "@app/hooks";
import useAttachmentValidator from "@investorRequest/hooks/useAttacmentValidator";
import FileUtils from "@shared/utils/FileUtils";
import { MimeType } from "@shared/models/mimeType";
import CommonUtils from "@shared/utils/CommonUtils";
import { Nullable } from "@shared/models/general";
import { ReactComponent as Clip } from "@assets/svg/clip.svg";
import { ReactComponent as ExcelIcon } from "@assets/svg/file_excel.svg";
import { ReactComponent as ImageIcon } from "@assets/svg/file_image.svg";
import { ReactComponent as PdfIcon } from "@assets/svg/file_pdf.svg";
import { ReactComponent as PowerpointIcon } from "@assets/svg/file_powerpoint.svg";
import { ReactComponent as WordIcon } from "@assets/svg/file_word.svg";
import { ReactComponent as DocumentIcon } from "@assets/svg/file_document.svg";

const WireInstructionsUploadRequest = () => {
  const { validationWarningShown, commonFields, customFields, submitSuccess } = useAppSelector(investorRequestSelector);
  const dispatch = useAppDispatch();
  const isMobile = useMobileLayout();
  const inputXS = isMobile ? 12 : 6;
  const [showSubmitAnotherDialog, setShowSubmitAnotherDialog] = useState(false);

  const { hasInternal, hasExternal } = useAppSelector((state) => selectInternalExternalPresence(state as any));
  const entitledFundsMap = useAppSelector(entitledFundsSelector).data;
  const entitledAccountsMap = useAppSelector(entitledAccountsSelector).data;
  const isHybrid = hasInternal && hasExternal;

  const isAdmin = useMemo(() => {
    if (!entitledFundsMap) {
      return false;
    }

    return Object.values(entitledFundsMap).some((fund) => fund.isAdmin === true);
  }, [entitledFundsMap]);

  const selectedAccounts = useAppSelector((state) => state.investorRequest.commonFields.selectedAccounts);
  const selectedFunds = useAppSelector((state) => state.investorRequest.commonFields.selectedFunds);
  const selectedAccountsMap = CommonUtils.convertArrayToMap(selectedAccounts);
  const selectedFundsMap = CommonUtils.convertArrayToMap(selectedFunds);

  const funds = useMemo<MultiselectOption[]>(() => {
    if (!entitledFundsMap) return [];

    const allFunds = CommonUtils.convertMapToArray(entitledFundsMap, (fund) => ({
      id: fund.globalId,
      label: fund.entityName,
      selected: !!selectedFundsMap[fund.globalId],
    }));

    return allFunds.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [entitledFundsMap, selectedFundsMap]);

  const accounts = useMemo<MultiselectOption[]>(() => {
    if (!entitledAccountsMap) return [];

    const allAccounts = CommonUtils.convertMapToArray(entitledAccountsMap, (account) => ({
      id: account.globalId,
      label: account.name,
      selected: !!selectedAccountsMap[account.globalId],
    }));

    return allAccounts.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [entitledAccountsMap, selectedAccountsMap]);

  const filteredFundsOptions = funds;
  const fetchedFundsRef = useRef<Set<string>>(new Set());

  const filteredAccountsOptions = useMemo(() => {
    if (!entitledAccountsMap) return accounts;

    if (isAdmin) {
      const adminAccounts = CommonUtils.convertMapToArray(entitledAccountsMap, (account) => ({
        id: account.globalId,
        label: account.name,
        selected: !!selectedAccountsMap[account.globalId],
      })).sort((a, b) => (a.label || "").localeCompare(b.label || ""));

      return adminAccounts;
    }

    if (!commonFields.selectedFunds || commonFields.selectedFunds.length === 0) {
      return [];
    }

    const allowedAccountIds = new Set<string>();

    commonFields.selectedFunds.forEach((fundId) => {
      const fund = entitledFundsMap?.[fundId];

      if (fund?.entitledAccountIds) {
        fund.entitledAccountIds.forEach((accountId) => {
          allowedAccountIds.add(accountId);
        });
      }
    });

    if (allowedAccountIds.size === 0) {
      if (isHybrid) {
        return accounts.map((account) => ({
          ...account,
          selected: !!selectedAccountsMap[account.id as string],
        }));
      }

      return accounts.map((account) => ({
        ...account,
        selected: !!selectedAccountsMap[account.id as string],
      }));
    }

    return CommonUtils.convertMapToArray(entitledAccountsMap, (account) => account)
      .filter((account) => allowedAccountIds.has(account.globalId))
      .map((account) => ({
        id: account.globalId,
        label: account.name,
        selected: !!selectedAccountsMap[account.globalId],
      }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [
    entitledAccountsMap,
    accounts,
    isAdmin,
    selectedAccountsMap,
    commonFields.selectedFunds,
    entitledFundsMap,
    isHybrid,
  ]);

  useEffect(() => {
    if (isAdmin) {
      return;
    }

    if (entitledFundsMap) {
      filteredFundsOptions.forEach((fundOption) => {
        const fund = entitledFundsMap[fundOption.id];
        const fundId = fundOption.id as string;

        if (fund && !fund.entitledAccountIds && !fetchedFundsRef.current.has(fundId)) {
          fetchedFundsRef.current.add(fundId);
          dispatch(fetchEntitledAccountsByEntity(fundId));
        }
      });
    }

    const currentSelected = commonFields.selectedAccounts;

    if (!currentSelected || currentSelected.length === 0) return;

    const validIds = new Set(filteredAccountsOptions.map((a) => a.id));
    const stillValid = currentSelected.filter((id) => validIds.has(id));

    if (stillValid.length !== currentSelected.length) {
      dispatch(updateCommonRequestData({ selectedAccounts: stillValid }));
    }
  }, [
    dispatch,
    filteredFundsOptions,
    entitledFundsMap,
    isAdmin,
    commonFields.selectedAccounts,
    filteredAccountsOptions,
  ]);

  useEffect(() => {
    if (!submitSuccess) return;

    setShowSubmitAnotherDialog(true);
  }, [submitSuccess]);

  const handleSubmitAnotherYes = useCallback(() => {
    setShowSubmitAnotherDialog(false);
    dispatch(clearSubmitSuccess());
    dispatch(updateCommonRequestData({ selectedFunds: [], selectedAccounts: [], message: "", attachment: null }));
    dispatch(updateCustomRequestData({ effectiveDate: "" }));
  }, [dispatch]);

  const handleSubmitAnotherNo = useCallback(() => {
    setShowSubmitAnotherDialog(false);
    dispatch(clearSubmitSuccess());
    dispatch(closeInvestorRequestDialog());
  }, [dispatch]);

  const { validateAttachment, allowedExtensions } = useAttachmentValidator();
  const accept = useMemo(
    () => `image/png, ${allowedExtensions.map((ext) => `.${ext}`).join(", ")}`,
    [allowedExtensions]
  );

  const AttachmentIcon = useMemo(() => {
    switch (commonFields.attachment?.type) {
      case MimeType.JPEG:
      case MimeType.PNG:
        return ImageIcon;
      case MimeType.PDF:
        return PdfIcon;
      case MimeType.DOCX:
        return WordIcon;
      case MimeType.PPTX:
        return PowerpointIcon;
      case MimeType.XLSX:
        return ExcelIcon;
      default:
        return DocumentIcon;
    }
  }, [commonFields.attachment]);

  const attachFile = useCallback(
    async (file: File) => {
      if (commonFields.attachment) return;
      if (!validateAttachment(file)) return;

      const base64 = await FileUtils.toBase64String(file);

      dispatch(
        updateCommonRequestData({
          attachment: {
            base64,
            name: file.name,
            type: file.type as MimeType,
          },
        })
      );
    },
    [commonFields.attachment, dispatch, validateAttachment]
  );

  const handleAttachFile = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      if (!event.target.files) return;

      const file = event.target.files[0];

      if (!file) return;

      await attachFile(file);
    },
    [attachFile]
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (commonFields.attachment) return;
      const file = event.dataTransfer.files?.[0];

      if (!file) return;

      await attachFile(file);
    },
    [attachFile, commonFields.attachment]
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const handleRemoveAttachment = useCallback(() => {
    dispatch(updateCommonRequestData({ attachment: null }));
  }, [dispatch]);

  const handleMessageChange = useCallback(
    (message: string) => dispatch(updateCommonRequestData({ message })),
    [dispatch]
  );

  const handleDatepickerChange = useCallback(
    (value: Nullable<Date>) =>
      dispatch(updateCustomRequestData({ effectiveDate: value ? toEffectiveDateString(value) : "" })),
    [dispatch]
  );

  return (
    <>
      <div data-testid="wire-instructions-upload-request" />
      <InvestorRequestEntitiesDropdowns
        required
        accounts={filteredAccountsOptions}
        funds={filteredFundsOptions}
        accountsDisabled={!isAdmin && commonFields.selectedFunds.length === 0}
      />
      <Grid item container xs={12} columnSpacing={isMobile ? undefined : 7} rowSpacing={isMobile ? 4 : 3}>
        <Grid
          item
          xs={inputXS}
          data-testid="investor-request-datepicker"
          sx={{
            "& [data-testid='datepicker-toggle-button']": {
              fontFamily: "inherit",
              fontSize: 20,
              fontWeight: 500,
              color: "text.primary",
            },
          }}
        >
          <FieldTooltip title="Date Instructions Become Effective" placement="top">
            <InvestorRequestDatepicker value={customFields.effectiveDate || ""} onChange={handleDatepickerChange} />
          </FieldTooltip>
        </Grid>
        <Grid item xs={12} sx={{ mt: isMobile ? 4 : 7 }}>
          <Box
            data-testid="wire-upload-dropzone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            border="2px dashed"
            borderColor={validationWarningShown && !commonFields.attachment ? "warning.main" : "divider"}
            borderRadius={2}
            bgcolor="common.white"
            px={isMobile ? 4 : 8}
            py={isMobile ? 6 : 8}
            textAlign="center"
            gap={2}
          >
            <Typography
              variant="bodySubhead2"
              sx={{ color: "grey.600", fontWeight: "medium", fontSize: 20, fontFamily: "inherit" }}
            >
              Drag and drop your document here, or click to browse
            </Typography>
            <Button
              upload
              size={isMobile ? "small" : "medium"}
              onClick={noop}
              shape="rounded"
              variant="outlined"
              startIcon={<Clip />}
              disabled={!!commonFields.attachment}
            >
              Choose file
              <input
                type="file"
                id="attachment-upload"
                name="attachment-upload"
                hidden
                accept={accept}
                value=""
                onChange={handleAttachFile}
              />
            </Button>
            <Typography
              variant="body2"
              sx={{ color: "grey.600", fontWeight: 400, fontSize: 14, fontFamily: "inherit" }}
            >
              Supported: PDF, JPG, PNG, DOCX
            </Typography>
          </Box>
          {validationWarningShown && !commonFields.attachment ? (
            <Box mt={1} color="warning.main" fontSize={12}>
              Please attach a document
            </Box>
          ) : null}
          {commonFields.attachment && (
            <Box mt={3}>
              <Chip Icon={AttachmentIcon} label={commonFields.attachment.name} onDelete={handleRemoveAttachment} />
              <Box fontSize={14} mt={2.5}>
                Up to one document may be attached; max file size of 5MB
              </Box>
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          <Box pt={isMobile ? undefined : 7} pb={0} mb={0}>
            <InvestorRequestTextarea
              placeholder="Please include any additional notes"
              value={commonFields.message}
              onChange={handleMessageChange}
            />
          </Box>
        </Grid>
      </Grid>
      <SubmitAnotherFormDialog
        open={showSubmitAnotherDialog}
        onYes={handleSubmitAnotherYes}
        onNo={handleSubmitAnotherNo}
      />
    </>
  );
};

export default memo(WireInstructionsUploadRequest);
